import { TokenUsage } from './msq'

export type SpeakStatusStage = 'generating' | 'audio'

export interface StatusEvent {
  type: 'status'
  stage: SpeakStatusStage
  time?: number
}

export interface TextDeltaEvent {
  type: 'text_delta'
  content: string
}

export interface MessageStopEvent {
  type: 'message_stop'
}

export interface AudioChunkEvent {
  type: 'audio_chunk'
  b64: string
}

export interface AudioStopEvent {
  type: 'audio_stop'
}

export interface ErrorEvent {
  type: 'error'
  message: string
}

export interface RunEvent {
  type: 'run'
  runId: string
}

export interface SnapshotEvent {
  type: 'snapshot'
  content: string
  usage?: TokenUsage
  status: 'running' | 'completed' | 'error'
  updatedAt: number
}

export interface UsageEvent {
  type: 'usage'
  promptTokens: number
  completionTokens: number
  totalTokens: number
}

export type SpeakEvent =
  | StatusEvent
  | TextDeltaEvent
  | MessageStopEvent
  | AudioChunkEvent
  | AudioStopEvent
  | ErrorEvent
  | RunEvent
  | SnapshotEvent
  | UsageEvent

// Type guards (no any)
export function isStatusEvent(e: unknown): e is StatusEvent {
  if (typeof e !== 'object' || e === null) return false
  const o = e as Record<string, unknown>
  if (o.type !== 'status') return false
  return o['stage'] === 'generating' || o['stage'] === 'audio'
}

export function isTextDeltaEvent(e: unknown): e is TextDeltaEvent {
  if (typeof e !== 'object' || e === null) return false
  const o = e as Record<string, unknown>
  return o.type === 'text_delta' && typeof o['content'] === 'string'
}

export function isMessageStopEvent(e: unknown): e is MessageStopEvent {
  if (typeof e !== 'object' || e === null) return false
  const o = e as Record<string, unknown>
  return o.type === 'message_stop'
}

export function isAudioChunkEvent(e: unknown): e is AudioChunkEvent {
  if (typeof e !== 'object' || e === null) return false
  const o = e as Record<string, unknown>
  return o.type === 'audio_chunk' && typeof o['b64'] === 'string'
}

export function isAudioStopEvent(e: unknown): e is AudioStopEvent {
  if (typeof e !== 'object' || e === null) return false
  const o = e as Record<string, unknown>
  return o.type === 'audio_stop'
}

export function isErrorEvent(e: unknown): e is ErrorEvent {
  if (typeof e !== 'object' || e === null) return false
  const o = e as Record<string, unknown>
  return o.type === 'error' && typeof o['message'] === 'string'
}

export function isRunEvent(e: unknown): e is RunEvent {
  if (typeof e !== 'object' || e === null) return false
  const o = e as Record<string, unknown>
  return o.type === 'run' && typeof o['runId'] === 'string' && o['runId'].length > 0
}

export function isSnapshotEvent(e: unknown): e is SnapshotEvent {
  if (typeof e !== 'object' || e === null) return false
  const o = e as Record<string, unknown>
  if (o.type !== 'snapshot') return false
  if (typeof o['content'] !== 'string') return false
  if (!['running', 'completed', 'error'].includes(String(o['status']))) return false
  if (typeof o['updatedAt'] !== 'number' || !Number.isFinite(o['updatedAt'] as number)) return false
  // usage is optional; if present, ensure it is an object
  if (o['usage'] !== undefined && (typeof o['usage'] !== 'object' || o['usage'] === null)) return false
  return true
}

export function isUsageEvent(e: unknown): e is UsageEvent {
  if (typeof e !== 'object' || e === null) return false
  const o = e as Record<string, unknown>
  if (o.type !== 'usage') return false
  return (
    typeof o['promptTokens'] === 'number' &&
    typeof o['completionTokens'] === 'number' &&
    typeof o['totalTokens'] === 'number'
  )
}

/**
 * Cached audio record (per assistant message)
 */
export interface CachedAudioRecordV1 {
  v: 1
  mime: string          // e.g., 'audio/webm; codecs=opus'
  b64: string           // base64 of the full, concatenated raw bytes (may be compressed via compressString)
  size: number          // uncompressed byte length
  createdAt: number     // epoch ms
  filename?: string     // optional default filename hint
}
